import React, { useState } from 'react';
import { Menu, X, Phone } from 'lucide-react';

interface NavbarProps {
  currentPage: 'home' | 'blog' | 'post' | 'contact' | 'resources' | 'products';
  onNavigate: (page: 'home' | 'blog' | 'contact' | 'resources' | 'products', sectionId?: string) => void;
}

const Navbar: React.FC<NavbarProps> = ({ currentPage, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);

  const homeLinks = [
    { name: 'Services', href: '#services' },
    { name: 'How It Works', href: '#process' },
    { name: 'Why Us', href: '#why-us' },
    { name: 'Testimonials', href: '#testimonials' },
  ];

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsOpen(false);
    
    // If it's a section link (starts with #)
    if (href.startsWith('#')) {
      const sectionId = href.substring(1); // remove #
      onNavigate('home', sectionId);
    } else if (href === '/blog') {
      onNavigate('blog');
    } else if (href === '/contact') {
      onNavigate('contact');
    } else if (href === '/resources') {
      onNavigate('resources');
    } else if (href === '/products') {
      onNavigate('products');
    } else {
        // Fallback for contact/other
        onNavigate('home', 'contact');
    }
  };

  const handleLogoClick = () => {
      onNavigate('home');
      window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center cursor-pointer" onClick={handleLogoClick}>
            <span className="text-3xl font-extrabold tracking-tight text-black">
              BuildMy<span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-700">Supplement</span>
            </span>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8 whitespace-nowrap">
            {/* If on home, show anchor links. If on blog, show "Back to Home" or simple links that redirect */}
            {homeLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleLinkClick(e, link.href)}
                className={`text-sm font-medium transition-colors ${
                    currentPage !== 'home' ? 'text-gray-400 hover:text-black' : 'text-gray-600 hover:text-orange-500'
                }`}
              >
                {link.name}
              </a>
            ))}

            <a
               href="/products"
               onClick={(e) => handleLinkClick(e, '/products')}
               className={`text-sm font-medium transition-colors ${
                currentPage === 'products' ? 'text-orange-600 font-bold' : 'text-gray-600 hover:text-orange-500'
               }`}
            >
                Products
            </a>

            <a
               href="/contact"
               onClick={(e) => handleLinkClick(e, '/contact')}
               className={`text-sm font-medium transition-colors ${
                currentPage === 'contact' ? 'text-orange-600 font-bold' : 'text-gray-600 hover:text-orange-500'
               }`}
            >
                Contact
            </a>

            <a
              href="/contact"
              onClick={(e) => handleLinkClick(e, '/contact')}
              className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent text-sm font-semibold rounded-md text-white bg-black hover:bg-orange-500 hover:text-black transition-colors duration-300 min-w-[200px]"
            >
              <Phone className="w-4 h-4 mr-2" />
              Book Consultation
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-500 hover:text-black focus:outline-none"
              aria-label={isOpen ? "Close menu" : "Open menu"}
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 absolute w-full shadow-lg">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {homeLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleLinkClick(e, link.href)}
                className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-orange-500 hover:bg-gray-50"
              >
                {link.name}
              </a>
            ))}
            <a
               href="/products"
               onClick={(e) => handleLinkClick(e, '/products')}
               className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-orange-500 hover:bg-gray-50"
            >
                Products
            </a>
            <a
               href="/contact"
               onClick={(e) => handleLinkClick(e, '/contact')}
               className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-orange-500 hover:bg-gray-50"
            >
                Contact
            </a>
            <a
              href="/contact"
              onClick={(e) => handleLinkClick(e, '/contact')}
              className="block w-full text-center mt-4 px-5 py-3 rounded-md font-bold text-black bg-orange-500 hover:bg-orange-600"
            >
              Book Free Consultation
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;